/* pzz-bundle-builder.js
 * ---------------------
 * Defines the <pzz-bundle-builder> custom element globally so it boots
 * automatically wherever the element appears — on the product page AND inside
 * Horizon's quick-add modal (which fetches the product page, extracts
 * [data-product-grid-content], and morphs it into the dialog; morphed <script>
 * tags do NOT execute, which is why the class must be defined globally here
 * rather than inline in the snippet).
 *
 * Load once, globally, via theme.liquid:
 *   <script src="{{ 'pzz-bundle-builder.js' | asset_url }}" defer></script>
 * (place it next to the pzz-overrides.css 
 */
class PzzBundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    const d = this.dataset;
    this.config = {
      bundleSize: parseInt(d.bundleSize, 10) || 1,
      freeProductQty: parseInt(d.freeProductQty, 10) || 0,
      discountCode: d.discountCode || 'DISCOUNT_CODE',
      giftPackaging: d.giftPackaging === 'true',
      autoIncludeGiftBox: d.autoIncludeGiftBox === 'true',
      bundleCollectionHandle: d.bundleCollection || '',
      freeProductCollectionHandle: d.freeProductCollection || '',
      bundleTotalPrice: parseFloat(d.bundleTotalPrice) || 0,
      bundleProductHandle: d.productHandle || '',
      bundleProductTitle: d.productTitle || '',
      storefrontToken: d.storefrontToken || '',
      shopDomain: d.shopDomain || '',
      packagingVariantId: d.packagingVariant || '',
      // Both in the shop's smallest currency unit (pence for GBP) — same
      // integer form Liquid's `product.price` already uses — so the summary
      // bar can add them and reformat without floating-point drift.
      bundlePriceCents: parseInt(d.bundlePrice, 10) || 0,
      packagingPriceCents: parseInt(d.packagingPrice, 10) || 0,
      // Plaintext banned brands (lowercased). A product is banned when its
      // inspired-by brand STARTS WITH any of these. Empty = block nothing.
      bannedBrands: (d.bannedBrands || '').toLowerCase().split(',').map((b) => b.trim()).filter(Boolean),
      // Size label for the gift wording, derived in Liquid from the free
      // collection handle (e.g. "15ml"); blank when the handle has no size.
      freeSize: (d.freeSize || '').trim()
    };

    this.autoGift = null;   // set when the gift slot collapses to a single-product card

    this.bundleProducts = [];
    this.freeProducts = [];
    this.activeSelect = null;
    this.searchInput = null;

    this.loadingEl = this.querySelector('[data-bundle-loading]');
    this.formEl = this.querySelector('[data-bundle-form]');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this._mqMobile = window.matchMedia('(max-width: 768px)');

   this.deferInit();   // was: this.init();
  }

  // Tear down the cart-change observer when the builder is removed (e.g. the
  // quick-add modal closes), so detached instances don't keep firing.
  disconnectedCallback() {
    if (this._cartObserver) this._cartObserver.disconnect();
    if (this._quickResizeObserver) this._quickResizeObserver.disconnect();
    clearTimeout(this._cartSyncTimer);
  }

  // Hold the Storefront API + /cart.js calls and the option rendering until the
  // builder is near the viewport. On the homepage it sits below the fold, so
  // none of that lands during initial load. When it's above the fold (its own
  // product page) or inside an opened quick-add modal, the observer fires
  // straight away, so behaviour there is unchanged.
  //
  // Card mode (the perfume-duo picker) is the one exception: its own page has
  // a "Build your duo" button ABOVE the fold that jumps straight to it, so
  // proximity-based lazy loading works against it — a shopper can click/scroll
  // there before the 600px margin ever triggers. Fetch the whole catalogue as
  // soon as the page has finished loading instead, so it's already sitting in
  // memory by the time they arrive, no visible fetch delay.
  deferInit() {
    if (this._initStarted) return;
    if (this.dataset.mode === 'card') {
      const start = () => { this._initStarted = true; this.init(); };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
      return;
    }
    if (!('IntersectionObserver' in window)) { this._initStarted = true; this.init(); return; }
    const io = new IntersectionObserver((entries, obs) => {
      if (entries.some((e) => e.isIntersecting)) {
        obs.disconnect();
        this._initStarted = true;
        this.init();
      }
    }, { rootMargin: '600px' });
    io.observe(this);
  }

  // PZZ INP: cached — slots are static after init() (nothing adds/removes
  // .bundle-builder__select elements later), so this used to be a fresh
  // querySelectorAll on every access, called 8+ times across the hot paths.
  get selects() {
    if (!this._selects) this._selects = Array.from(this.querySelectorAll('.bundle-builder__select'));
    return this._selects;
  }

  generateBundleId() {
    return Math.random().toString(36).substring(2, 5).toUpperCase();
  }

  /* ---- display / search helpers ---------------------------------------- */

  // Fallback code resolver (used only when the title isn't canonical). Takes the
  // leading number + first letter run ONLY, so a slug tail like
  // "309-lo-inspired-by-love" can never leak past "309 LO". A code-less handle
  // like "261" yields "261".
  handleToCode(handle) {
    const h = (handle || '').replace(/-\d+\s*ml$/i, '');
    const m = h.match(/^(\d+)-([a-z]+)/i);
    if (m) return (m[1] + ' ' + m[2]).toUpperCase();
    return h.replace(/-/g, ' ').toUpperCase().trim();
  }

  // Canonical Zag code. The renamed product title is the source of truth
  // ("ZAG Zodiak 309 LO Eau de Parfum 100ml" -> "309 LO"); the code group is
  // optional so a title without one ("...261 Eau de Parfum...") yields just
  // "261". The pattern anchors before "Eau de Parfum" so a code-less title
  // stops at the number instead of grabbing "Eau". Falls back to the hardened
  // handle only when the title isn't in canonical form.
  productCode(product) {
    const mt = ((product && product.title) || '')
      .match(/ZAG\s+Zodiak\s+(\d+)(?:\s+([A-Za-z]+))?\s+Eau\s+de\s+Parfum/i);
    if (mt) return (mt[2] ? mt[1] + ' ' + mt[2] : mt[1]).toUpperCase();
    return this.handleToCode(product ? product.handle : '');
  }

  isZag(product) {
    return /zag/i.test(product.vendor || '');
  }

  markBanned(list) {
    const terms = this.config.bannedBrands;
    list.forEach((p) => {
      const brand = (p.inspiredBrand || '').toLowerCase().trim();
      p.banned = !!brand && terms.some((t) => brand.startsWith(t));
    });
  }

  // The visible label for an option, per the four-case rule.
displayLabel(product) {
  const mobile = this._mqMobile.matches;  
  if (!this.isZag(product)) return product.title || '';
  let label = (mobile ? '' : 'ZAG ') + this.productCode(product);
  if (product.inspiredName && !product.banned) {
    label += ' · inspired by ' + product.inspiredName;
  }
  return label;
}

  // The hidden search string: code + name + brand for Zag (brand included so a
  // banned product stays findable), else the title.
  searchString(product) {
    if (!this.isZag(product)) return (product.title || '').toLowerCase();
    return [
      this.productCode(product),
      product.inspiredName || '',
      product.inspiredBrand || ''
    ].join(' ').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  // Resize a Shopify CDN image URL by setting its width param (keeps the ?v= hash).
  sizedImage(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /* ---- gift slot helpers ----------------------------------------------- */

  // Is the gift collection perfumes, and at what size? Perfume-ness comes from
  // productType containing "perfume"; size from the free collection handle
  // (Liquid → data-free-size), else the product's own -NNml handle suffix, else
  // the 100ml default for a perfume with no suffix. Otherwise size is blank.
  giftMeta() {
    const first = this.freeProducts[0];
    const isPerfume = !!first && /perfume/i.test(first.productType || '');
    let size = this.config.freeSize;
    if (!size && first) {
      const m = (first.handle || '').match(/-(\d+\s*ml)$/i);
      if (m) size = m[1];
      else if (isPerfume) size = '100ml';
    }
    return { isPerfume, size };
  }

  // The gift heading element (the label directly before the free slot).
  giftLabel() {
    const freeSlot = this.querySelector('[data-bundle-slot="free"]');
    const row = freeSlot ? freeSlot.closest('.bundle-builder__option-row') : null;
    let el = row ? row.previousElementSibling : null;
    while (el && !el.classList.contains('bundle-builder__label')) el = el.previousElementSibling;
    return el || null;
  }

  applyGiftHeading(mode) {
    const label = this.giftLabel();
    if (!label) return;
    const { isPerfume, size } = this.giftMeta();
    const sized = isPerfume && size;
    if (mode === 'card') {
      label.textContent = sized ? `Your free ${size} perfume` : 'Your free gift';
    } else {
      label.textContent = sized ? `And your ${size} gift:` : 'And your gift:';
    }
  }

  // Bestseller-style card for a single auto-included gift product.
  buildGiftCard(product) {
    const isZag = this.isZag(product);
    const title = isZag ? ('ZAG ' + this.productCode(product)) : (product.title || '');
    const showInspired = isZag && product.inspiredName && !product.banned;
    const imgSrc = product.featuredImage ? this.sizedImage(product.featuredImage, 160) : '';

    const card = document.createElement('div');
    card.className = 'bundle-builder__gift-card';
    card.innerHTML =
      '<div class="bundle-builder__gift-card-img">' +
        (imgSrc ? '<img src="' + this.escapeHtml(imgSrc) + '" alt="' + this.escapeHtml(title) + '" loading="lazy" width="80" height="80">' : '') +
      '</div>' +
      '<div class="bundle-builder__gift-card-info">' +
        '<span class="bundle-builder__gift-card-badge">Free gift</span>' +
        '<p class="bundle-builder__gift-card-title">' + this.escapeHtml(title) + '</p>' +
        (showInspired ? '<p class="bundle-builder__gift-card-inspired">Inspired by ' + this.escapeHtml(product.inspiredName) + '</p>' : '') +
        '<p class="bundle-builder__gift-card-price">FREE</p>' +
      '</div>' +
      '<div class="bundle-builder__gift-card-check" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>' +
      '</div>';
    return card;
  }

  // Collapse the free dropdown(s) into a single auto-included gift card.
  renderGiftCard() {
    const product = this.freeProducts[0];
    if (!product || !product.variants || !product.variants.length) return false;
    const variant = product.variants[0];
    this.autoGift = { id: variant.id, title: this.displayLabel(product) };

    const freeRows = Array.from(this.querySelectorAll('[data-bundle-slot="free"]'))
      .map((s) => s.closest('.bundle-builder__option-row') || s);
    if (!freeRows.length) return false;

    const anchor = freeRows[0];
    anchor.parentNode.insertBefore(this.buildGiftCard(product), anchor);
    freeRows.forEach((r) => r.remove());
    return true;
  }

  // Stock chip at the right of an option (before the pill): amber "● N left",
  // grey "Sold out", or hidden when stock is healthy.
  setStockChip(option, state, qty) {
    const chip = option.querySelector('.bundle-builder__stock-chip');
    if (!chip) return;
    chip.className = 'bundle-builder__stock-chip';
    if (state === 'soldout') {
      chip.classList.add('bundle-builder__stock-chip--soldout');
      chip.textContent = 'Sold out';
      chip.hidden = false;
    } else if (state === 'low') {
      chip.classList.add('bundle-builder__stock-chip--low');
      chip.innerHTML = '<span class="bundle-builder__stock-dot"></span>' + qty + ' left';
      chip.hidden = false;
    } else {
      chip.textContent = '';
      chip.hidden = true;
    }
  }

  /* ---- stock helpers --------------------------------------------------- */

  isVariantOutOfStock(variant) {
    if (!variant.availableForSale) return true;
    if (variant.quantityAvailable !== null && variant.quantityAvailable <= 0) return true;
    return false;
  }

  getVariantStockData(variant) {
    return {
      stock: variant.quantityAvailable || 0,
      available: variant.availableForSale ? 'true' : 'false',
      inventoryManaged: variant.quantityAvailable !== null ? 'true' : 'false',
      inventoryPolicy: 'deny'
    };
  }

  async fetchAllProducts(collectionHandle) {
    const { storefrontToken, shopDomain } = this.config;
    let allProducts = [];
    let hasNextPage = true;
    let cursor = null;

    const query = `
      query getCollectionProducts($handle: String!, $first: Int!, $after: String) {
        collectionByHandle(handle: $handle) {
          products(first: $first, after: $after) {
            edges {
              node {
                id
                title
                productType
                vendor
                tags
                handle
                featuredImage { url }
                metafields(identifiers: [
                  {namespace: "custom", key: "pzz_inspired_name"},
                  {namespace: "custom", key: "pzz_inspired_brand"}
                ]) {
                  key
                  value
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      quantityAvailable
                      price { amount }
                    }
                  }
                }
              }
              cursor
            }
            pageInfo { hasNextPage }
          }
        }
      }
    `;

    while (hasNextPage) {
      try {
        const variables = { handle: collectionHandle, first: 250, after: cursor };
        const response = await fetch(`https://${shopDomain}/api/2024-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontToken
          },
          body: JSON.stringify({ query, variables })
        });
        const data = await response.json();

        if (data.errors) {
          console.error('GraphQL errors:', data.errors);
          break;
        }

        if (data.data?.collectionByHandle?.products?.edges) {
          const edges = data.data.collectionByHandle.products.edges;
          const products = edges.map((edge) => {
            const p = edge.node;
            // Flatten the metafields list (may contain nulls) into a lookup.
            const metaMap = {};
            (p.metafields || []).forEach((m) => { if (m && m.key) metaMap[m.key] = m.value; });
            return {
              id: p.id,
              title: p.title,
              productType: p.productType,
              vendor: p.vendor,
              tags: p.tags,
              handle: p.handle,
              featuredImage: p.featuredImage ? p.featuredImage.url : '',
              inspiredName: metaMap['pzz_inspired_name'] || '',
              inspiredBrand: metaMap['pzz_inspired_brand'] || '',
              variants: p.variants.edges.map((ve) => {
                const v = ve.node;
                return {
                  id: v.id.split('/').pop(),
                  title: v.title,
                  availableForSale: v.availableForSale,
                  quantityAvailable: v.quantityAvailable,
                  price: parseFloat(v.price.amount),
                  available: v.availableForSale,
                  inventory_quantity: v.quantityAvailable,
                  inventory_management: v.quantityAvailable !== null ? 'shopify' : null,
                  inventory_policy: 'deny'
                };
              })
            };
          });

          allProducts = allProducts.concat(products);
          hasNextPage = data.data.collectionByHandle.products.pageInfo.hasNextPage;
          if (hasNextPage && edges.length > 0) {
            cursor = edges[edges.length - 1].cursor;
          } else {
            hasNextPage = false;
          }
        } else {
          hasNextPage = false;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Error fetching products from Storefront API:', error);
        hasNextPage = false;
      }
    }

    return allProducts;
  }

  async fetchAllProductsREST(collectionHandle) {
    console.log('Falling back to REST API — inventory + metafields will be limited');
    let allProducts = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      try {
        const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${page}`);
        const data = await response.json();
        if (data.products && data.products.length > 0) {
          const transformed = data.products.map((product) => ({
            ...product,
            // REST has no metafields — labels fall back to code/title only.
            inspiredName: '',
            inspiredBrand: '',
            featuredImage: (product.images && product.images[0] && product.images[0].src) || '',
            variants: product.variants.map((variant) => ({
              ...variant,
              availableForSale: variant.available,
              quantityAvailable: null,
              inventory_quantity: null,
              inventory_management: null,
              inventory_policy: 'continue'
            }))
          }));
          allProducts = allProducts.concat(transformed);
          page++;
          hasMore = data.products.length === 250;
        } else {
          hasMore = false;
        }
      } catch (error) {
        console.error('Error fetching products from REST API:', error);
        hasMore = false;
      }
    }

    return allProducts;
  }

  naturalCompare(a, b) {
    const ax = [];
    const bx = [];
    a.replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
      ax.push([$1 || Infinity, $2 || '', $1 ? $1.length : 0, $1 ? $1.startsWith('0') : false]);
    });
    b.replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
      bx.push([$1 || Infinity, $2 || '', $1 ? $1.length : 0, $1 ? $1.startsWith('0') : false]);
    });
    while (ax.length && bx.length) {
      const an = ax.shift();
      const bn = bx.shift();
      if (an[0] !== Infinity && bn[0] !== Infinity) {
        if (an[3] && !bn[3]) return -1;
        if (!an[3] && bn[3]) return 1;
        const numCompare = parseInt(an[0], 10) - parseInt(bn[0], 10);
        if (numCompare !== 0) return numCompare;
        if (an[2] !== bn[2]) return an[2] - bn[2];
      } else {
        const textCompare = an[1].localeCompare(bn[1]);
        if (textCompare !== 0) return textCompare;
      }
    }
    return ax.length - bx.length;
  }

  async fetchFromMultipleCollections(handlesString) {
    if (!handlesString) return [];
    const handles = handlesString.split(',').map((h) => h.trim()).filter(Boolean);
    // PZZ INP: fetch every collection concurrently instead of one at a time —
    // same dedup, just merged in handle order once all requests have settled
    // rather than each one gated behind the last.
    const results = await Promise.all(handles.map((handle) => this.fetchAllProducts(handle)));
    const all = [];
    const seen = new Set();
    results.forEach((products) => {
      products.forEach((product) => {
        if (!seen.has(product.id)) {
          seen.add(product.id);
          all.push(product);
        }
      });
    });
    return all;
  }
// Icon from the shared sprite (emitted once by pzz-load via
  // {% render 'pzz-gender-pill', part: 'assets' %}). Single source — the fixed
  // unisex glyph and any future tweaks flow through here automatically.
  genderIcon(type) {
    if (!type) return '';
    return '<svg class="pzz-gender-pill__svg" aria-hidden="true"><use href="#pzz-gender-icon-' + type + '"></use></svg>';
  }
  // Builds ONE option row (unattached — caller decides when to mount it).
  // Pure extraction from the old all-at-once populateSelectOptions loop body;
  // same markup/attributes/JS-prop-caching, no behaviour change there.
  buildOptionEl(product, label) {
    const variant = product.variants[0];
    const isOutOfStock = this.isVariantOutOfStock(variant);
    const stockData = this.getVariantStockData(variant);
    const search = this.searchString(product);

    // Split display data — same fields displayLabel() already merges into one
    // string, kept separate here so the row can show them on two lines (and so
    // card mode can pull them back out later without re-touching `product`).
    const isZagProduct = this.isZag(product);
    const mobile = this._mqMobile.matches;
    const shortTitle = isZagProduct ? ((mobile ? '' : 'ZAG ') + this.productCode(product)) : (product.title || '');
    // Full "ZAG Zodiak 019 AN" form — card mode's filled title only, never the
    // dropdown row (which keeps the shorter shortTitle above; too long for a
    // 40px-tall search result line).
    const longTitle = isZagProduct ? ('ZAG Zodiak ' + this.productCode(product)) : (product.title || '');
    const inspiredText = (isZagProduct && product.inspiredName && !product.banned) ? ('Inspired by ' + product.inspiredName) : '';
    const thumbSrc = product.featuredImage ? this.sizedImage(product.featuredImage, 120) : '';
    const cardImgSrc = product.featuredImage ? this.sizedImage(product.featuredImage, 900) : '';

    const optionDiv = document.createElement('div');
    optionDiv.className = `bundle-builder__option${isOutOfStock ? ' bundle-builder__option--out-of-stock' : ''}`;
    optionDiv.setAttribute('data-value', variant.id);
    optionDiv.setAttribute('data-stock', stockData.stock);
    optionDiv.setAttribute('data-search', search);
    optionDiv.setAttribute('data-available', stockData.available);
    optionDiv.setAttribute('data-inventory-managed', stockData.inventoryManaged);
    optionDiv.setAttribute('data-inventory-policy', stockData.inventoryPolicy);
    optionDiv.setAttribute('data-product-handle', product.handle);
    optionDiv.setAttribute('data-product-title', label);
    optionDiv.setAttribute('data-original-text', label);

    // PZZ INP: immutable fields precomputed once as JS properties — read
    // directly by disableOptions()/isOptionOutOfStock() instead of
    // getAttribute+parseInt on every call (same expando-caching pattern as
    // the existing _pzzSig below). data-cart-quantity/_pzzCartQty is the one
    // mutable field here; fetchCartItems() is the only thing that changes it
    // afterwards — but a row can now be BUILT well after init() (lazy batch
    // on scroll, or all-at-once on first search keystroke), so seed it from
    // whatever cart state is already known instead of always assuming 0.
    optionDiv._pzzValue = variant.id;
    optionDiv._pzzStock = stockData.stock;
    optionDiv._pzzAvailable = stockData.available === 'true';
    optionDiv._pzzInventoryManaged = stockData.inventoryManaged === 'true';
    const knownQty = (this._lastCartQuantities && this._lastCartQuantities[variant.id]) || 0;
    optionDiv._pzzCartQty = knownQty;
    if (knownQty) optionDiv.setAttribute('data-cart-quantity', knownQty);

    // Cached for card-mode selection (fillCard reads these back off the
    // clicked option element instead of re-deriving from `product`).
    optionDiv._pzzTitle = shortTitle;
    optionDiv._pzzCardTitle = longTitle;
    optionDiv._pzzInspired = inspiredText;
    optionDiv._pzzCardImage = cardImgSrc;

    let pillLabel = '';
    let pillType = '';
    if (product.productType) {
      const parts = product.productType.split(',').map((p) => p.trim().toLowerCase());
      // unisex -> women -> men ("women" contains "men", so men is tested last)
      if (parts.includes('unisex'))     { pillType = 'unisex'; pillLabel = 'Unisex'; }
      else if (parts.includes('women')) { pillType = 'women';  pillLabel = 'Women'; }
      else if (parts.includes('men'))   { pillType = 'men';    pillLabel = 'Men'; }
    }
    optionDiv._pzzPillType = pillType;
    optionDiv._pzzPillLabel = pillLabel;

    const containerDiv = document.createElement('div');
    containerDiv.className = 'bundle-builder__option-container';

    if (thumbSrc) {
      const imgEl = document.createElement('img');
      imgEl.className = 'bundle-builder__option-image';
      imgEl.src = thumbSrc;
      imgEl.alt = '';
      imgEl.loading = 'lazy';
      imgEl.width = 40;
      imgEl.height = 40;
      containerDiv.appendChild(imgEl);
    }

    const contentDiv = document.createElement('div');
    contentDiv.className = 'bundle-builder__option-content';
    contentDiv.setAttribute('title', label);

    const titleLine = document.createElement('div');
    titleLine.className = 'bundle-builder__option-title';
    titleLine.textContent = shortTitle;
    contentDiv.appendChild(titleLine);

    if (inspiredText) {
      const inspiredLine = document.createElement('div');
      inspiredLine.className = 'bundle-builder__option-inspired';
      inspiredLine.textContent = inspiredText;
      contentDiv.appendChild(inspiredLine);
    }

    const typeContainer = document.createElement('div');
    typeContainer.className = 'bundle-builder__option-type-container';

    const chip = document.createElement('span');
    chip.className = 'bundle-builder__stock-chip';
    chip.hidden = true;
    typeContainer.appendChild(chip);

    if (pillType) {
      const pill = document.createElement('span');
      pill.className = 'pzz-gender-pill pzz-gender-pill--' + pillType + ' pzz-gender-pill--compact';
      pill.setAttribute('aria-label', pillLabel);
      pill.innerHTML =
        '<span class="pzz-gender-pill__text">' + pillLabel + '</span>' +
        '<span class="pzz-gender-pill__icon" aria-hidden="true">' + this.genderIcon(pillType) + '</span>';
      typeContainer.appendChild(pill);
    }

    containerDiv.appendChild(contentDiv);
    containerDiv.appendChild(typeContainer);
    optionDiv.appendChild(containerDiv);

    // Chip state operates on the not-yet-inserted node — no reflow until the
    // caller appends it.
    if (isOutOfStock) {
      optionDiv.style.pointerEvents = 'none';
      this.setStockChip(optionDiv, 'soldout');
    } else if (stockData.inventoryManaged === 'true' && stockData.stock > 0 && stockData.stock < 4) {
      this.setStockChip(optionDiv, 'low', stockData.stock);
    }

    return optionDiv;
  }

  // PZZ INP: how many rows to mount per batch — enough to comfortably
  // overfill the dropdown's 500px visible height (~28px/row), so the first
  // batch alone makes the panel scrollable without a second batch yet.
  static get OPTION_BATCH_SIZE() { return 40; }

  // Mounts the next unbuilt batch for a select. Safe to call when nothing's
  // left — no-ops. Newly-mounted rows only know their own stock, so this
  // re-triggers the (now cheap, cached-array) cross-select rescan.
  renderOptionBatch(selectElement) {
    const decorated = selectElement._pzzDecorated || [];
    const have = selectElement._pzzRenderedCount || 0;
    if (have >= decorated.length) { selectElement._pzzAllMounted = true; return; }

    const optionsContainer = selectElement.querySelector('.bundle-builder__options');
    const next = decorated.slice(have, have + PzzBundleBuilder.OPTION_BATCH_SIZE);
    const frag = document.createDocumentFragment();
    // A quick pick can select a product whose row isn't mounted yet; mark it
    // as it mounts so the highlight still appears when the shopper scrolls.
    const triggerEl = selectElement.querySelector('.bundle-builder__select-trigger');
    const selectedValue = triggerEl ? triggerEl.getAttribute('data-selected-value') : null;
    next.forEach(({ product, label }) => {
      const el = this.buildOptionEl(product, label);
      if (selectedValue && String(el._pzzValue) === selectedValue) el.classList.add('bundle-builder__option--selected');
      frag.appendChild(el);
      selectElement._pzzOptionEls.push(el);
    });
    optionsContainer.appendChild(frag);
    selectElement._pzzRenderedCount = have + next.length;
    selectElement._pzzAllMounted = selectElement._pzzRenderedCount >= decorated.length;

    this.scheduleDisableOptions();
  }

  // Mounts everything remaining — used the moment the shopper starts typing,
  // so search always finds every match. Windowing only ever skips building
  // rows the shopper hasn't scrolled to yet; it never skips search results.
  mountAllOptions(selectElement) {
    while (!selectElement._pzzAllMounted) this.renderOptionBatch(selectElement);
  }

  populateSelectOptions(selectElement, products) {
    const optionsContainer = selectElement.querySelector('.bundle-builder__options');
    optionsContainer.innerHTML = '';

    // Decorate-sort-undo: compute displayLabel ONCE per product, then sort on
    // the cached key. Previously the comparator called displayLabel twice per
    // comparison (O(n log n) matchMedia + regex), and the render loop called it
    // a third time per product. Now it's one pass.
    //
    // PZZ INP: memoized per products array (bundleProducts / freeProducts) —
    // every slot sharing the same product list (bundleSize slots) used to redo
    // this decorate+sort from scratch; now it's computed once and reused.
    if (!this._decoratedCache) this._decoratedCache = new WeakMap();
    let decorated = this._decoratedCache.get(products);
    if (!decorated) {
      decorated = products
        .filter((p) => p.variants && p.variants.length > 0)
        .map((product) => ({ product, label: this.displayLabel(product) }));
      decorated.sort((a, b) => this.naturalCompare(a.label, b.label));
      this._decoratedCache.set(products, decorated);
    }

    // PZZ INP: mount only the first batch now — laying out and painting all
    // ~300 rows the instant the dropdown opens was the confirmed remaining
    // bottleneck (independent of backdrop-filter — tested). The rest mount
    // lazily on scroll (renderOptionBatch, wired to the panel's own scroll in
    // setupEventListeners) or all at once the moment the shopper starts
    // typing (mountAllOptions), so search is never missing a result.
    selectElement._pzzDecorated = decorated;
    selectElement._pzzRenderedCount = 0;
    selectElement._pzzOptionEls = [];
    selectElement._pzzAllMounted = false;
    this.renderOptionBatch(selectElement);
  }

  async init() {
    this.loadingEl.style.display = 'block';
    try {
      this.bundleProducts = await this.fetchFromMultipleCollections(this.config.bundleCollectionHandle);

      if (this.config.freeProductCollectionHandle && this.config.freeProductQty > 0) {
        this.freeProducts = await this.fetchFromMultipleCollections(this.config.freeProductCollectionHandle);
      }

      // Resolve banned-brand status before any rendering so labels are correct.
      this.markBanned(this.bundleProducts);
      this.markBanned(this.freeProducts);

      if (this.bundleProducts.length < this.config.bundleSize) {
        this.errorEl.style.display = 'block';
        this.loadingEl.style.display = 'none';
        return;
      }

      this.querySelectorAll('[data-bundle-slot="product"]').forEach((slot) => {
        this.populateSelectOptions(slot, this.bundleProducts);
      });

      if (this.config.freeProductQty > 0 && this.freeProducts.length > 0) {
        if (this.freeProducts.length === 1) {
          // Single product in the gift collection → bestseller-style card,
          // auto-included like the gift box; no dropdown to choose from.
          this.renderGiftCard();
          this.applyGiftHeading('card');
        } else {
          this.querySelectorAll('[data-bundle-slot="free"]').forEach((slot) => {
            this.populateSelectOptions(slot, this.freeProducts);
          });
          this.applyGiftHeading('dropdown');
        }
      }

      this.formEl.style.display = 'block';
      this.loadingEl.style.display = 'none';
      // Straight after the form is shown (not after the /cart.js await below),
      // so the measured chip layout is the first one painted. A pick made
      // before the cart sync lands still works; it just can't see cart stock yet.
      this.setupQuickPicks();

      this.setupGiftToggle();
      await this.fetchCartItems();
      this.setupEventListeners();
      this.watchCartChanges();   // keep availability in sync with external cart edits
    } catch (error) {
      console.error('Error initialising bundle form:', error);
      this.errorEl.style.display = 'block';
      this.loadingEl.style.display = 'none';
    }
  }

  setupGiftToggle() {
    const toggle = this.querySelector('.bundle-builder__gift-toggle');
    const checkbox = this.querySelector('[data-gift-checkbox]');
    if (toggle && checkbox) {
      checkbox.addEventListener('change', () => {
        toggle.classList.toggle('bundle-builder__gift-toggle--active', checkbox.checked);
        this.updateSummaryPrice();
      });
    }
    // Covers the auto-include case too (no checkbox, price always includes
    // packaging) and sets the correct baseline on first render either way.
    this.updateSummaryPrice();
  }

  // Card mode's summary price — recomputed whenever the optional gift-box
  // toggle changes, so "Add to bag" never shows a total that's missing the
  // packaging charge the shopper just opted into. No-ops if the bar isn't in
  // the markup (compact mode never renders it).
  updateSummaryPrice() {
    const priceEl = this.querySelector('[data-bundle-summary-price]');
    if (!priceEl) return;
    const checkbox = this.querySelector('[data-gift-checkbox]');
    const giftIncluded = this.config.autoIncludeGiftBox || !!(checkbox && checkbox.checked);
    const totalCents = this.config.bundlePriceCents + (giftIncluded ? this.config.packagingPriceCents : 0);
    priceEl.textContent = '£' + (totalCents / 100).toFixed(2);
  }

  isOptionOutOfStock(option, totalInUse = 0) {
    const isAvailable = option._pzzAvailable !== undefined ? option._pzzAvailable : option.getAttribute('data-available') === 'true';
    const isInventoryManaged = option._pzzInventoryManaged !== undefined ? option._pzzInventoryManaged : option.getAttribute('data-inventory-managed') === 'true';
    const optionStock = option._pzzStock !== undefined ? option._pzzStock : parseInt(option.getAttribute('data-stock') || 0, 10);
    if (!isAvailable) return true;
    if (isInventoryManaged) return optionStock <= 0 || totalInUse >= optionStock;
    return false;
  }

    disableOptions() {
    // PZZ INP: this.selects is a cached getter now (no repeat DOM query here).
    const selects = this.selects;
    const selectedValues = selects
      .map((s) => s.querySelector('.bundle-builder__select-trigger').getAttribute('data-selected-value'))
      .filter(Boolean);

    const selectedCounts = {};
    selectedValues.forEach((v) => { selectedCounts[v] = (selectedCounts[v] || 0) + 1; });

    selects.forEach((select) => {
      const currentTrigger = select.querySelector('.bundle-builder__select-trigger');
      const currentlySelectedValue = currentTrigger.getAttribute('data-selected-value');

      // PZZ INP: cached option array (§ populateSelectOptions) instead of a
      // fresh querySelectorAll over ~300 nodes on every call.
      (select._pzzOptionEls || []).forEach((option) => {
        const optionValue = option._pzzValue;
        const optionStock = option._pzzStock !== undefined ? option._pzzStock : 999999;
        const cartQuantity = option._pzzCartQty || 0;
        const isAvailable = option._pzzAvailable;
        const isInventoryManaged = option._pzzInventoryManaged;

        const selectedInForm = selectedCounts[optionValue] || 0;
        const totalInUse = cartQuantity + selectedInForm;
        const isCurrentSelection = optionValue === currentlySelectedValue;
        const adjustedTotalInUse = isCurrentSelection ? Math.max(0, totalInUse - 1) : totalInUse;

        let isOutOfStock = false;
        if (!isAvailable) {
          isOutOfStock = true;
        } else if (isInventoryManaged && !isCurrentSelection) {
          if (optionStock <= 0) isOutOfStock = true;
          else if (adjustedTotalInUse >= optionStock) isOutOfStock = true;
        }

        // Same chip logic as before, just computed before writing.
        let chipState = 'none', chipQty = 0;
        if (isOutOfStock) {
          chipState = 'soldout';
        } else if (isInventoryManaged) {
          const displayStock = optionStock - totalInUse;
          if (optionStock > 0 && displayStock < 4 && displayStock > 0) {
            chipState = 'low';
            chipQty = displayStock;
          }
        }

        // PZZ INP: nothing changed for this option → no DOM writes at all.
        const sig = (isOutOfStock ? 'x' : 'o') + '|' + chipState + '|' + chipQty;
        if (option._pzzSig === sig) return;
        option._pzzSig = sig;

        option.classList.toggle('bundle-builder__option--out-of-stock', isOutOfStock);
        option.style.pointerEvents = isOutOfStock ? 'none' : '';
        this.setStockChip(option, chipState, chipQty);
      });
    });
  }
  // PZZ INP: coalesce + defer the stock rescan out of the interaction frame.
  scheduleDisableOptions() {
    if (this._disablePending) return;
    this._disablePending = true;
    requestAnimationFrame(() => {
      this._disablePending = false;
      this.disableOptions();
    });
  }
  async fetchCartItems() {
    try {
      const cartItems = await fetch('/cart.js')
        .then((response) => response.json())
        .then((data) => data.items)
        .catch(() => []);

      const cartQuantities = {};
      cartItems.forEach((item) => {
        const variantId = item.variant_id.toString();
        cartQuantities[variantId] = (cartQuantities[variantId] || 0) + item.quantity;
      });

      // PZZ INP: diff against the last known quantities before touching
      // anything. watchCartChanges() re-triggers this on ANY cart mutation
      // anywhere on the page — a quantity stepper on a completely unrelated
      // product included — and most of those don't touch this bundle's
      // specific variant IDs at all. Skipping the full option rescan when
      // nothing relevant changed is what actually fixes "every field input
      // triggers it": most calls now become one cheap object comparison.
      const previous = this._lastCartQuantities || {};
      let changed = false;
      const allValues = new Set([...Object.keys(previous), ...Object.keys(cartQuantities)]);
      allValues.forEach((v) => { if ((previous[v] || 0) !== (cartQuantities[v] || 0)) changed = true; });
      this._lastCartQuantities = cartQuantities;

      if (changed) {
        this.selects.forEach((select) => {
          (select._pzzOptionEls || []).forEach((option) => {
            const qty = cartQuantities[option._pzzValue] || 0;
            if (option._pzzCartQty === qty) return;
            option._pzzCartQty = qty;
            option.setAttribute('data-cart-quantity', qty);
          });
        });
        this.scheduleDisableOptions();
      }

      // Pick up any cart section that has appeared since the last sync
      // (e.g. the drawer rendered for the first time after an add).
      this.attachCartObservers();
    } catch (error) {
      console.error('Error fetching cart items:', error);
    }
  }

  /* ---- external cart-change sync --------------------------------------- */
  // The builder used to know only about cart changes it made itself, so removing
  // a bundle from the drawer or cart page left its availability stale. Watch the
  // cart section(s) for any mutation and re-read /cart.js. Deliberately DOM-based
  // rather than event-based: it doesn't depend on Horizon's cart event names,
  // which have changed between theme versions.
  watchCartChanges() {
    if (this._cartObserver) return;
    this._cartObserver = new MutationObserver(() => {
      // Debounce: a single cart edit produces a burst of morph mutations, and
      // the builder's own add already re-reads — collapse them into one /cart.js.
      clearTimeout(this._cartSyncTimer);
      this._cartSyncTimer = setTimeout(() => this.fetchCartItems(), 150);
    });
    this.attachCartObservers();
  }

  // Observe the stable <section id="shopify-section-…"> wrapper around each
  // cart-items-component, so even a full re-render of the component (not just
  // child edits) is caught. Idempotent — safe to call repeatedly as new cart
  // sections appear.
  attachCartObservers() {
    if (!this._cartObserver) return;
    if (!this._observedCartTargets) this._observedCartTargets = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const target = el.closest('[id^="shopify-section"]') || el;
      if (this._observedCartTargets.has(target)) return;
      this._observedCartTargets.add(target);
      this._cartObserver.observe(target, { childList: true, subtree: true });
    });
  }

  resetSearch(select) {
    if (!this.searchInput) return;
    this.searchInput.value = '';
    // PZZ INP: only clear options that are actually hidden. Untouched options
    // (inline display '') and already-visible ones need no write, so reopening
    // a field no longer writes to every node.
    (select._pzzOptionEls || []).forEach((option) => {
      if (option.style.display === 'none') option.style.display = 'block';
    });
  }

  // `input` is optional — compact mode has one shared, singleton search input
  // (this.searchInput, swapped in/out on open/close); card mode's panels are
  // all visible and filterable at once, so each passes its OWN static input
  // explicitly instead of fighting over a shared one.
  filterOptions(select, input) {
    const activeInput = input || this.searchInput;
    if (!activeInput) return;
    // Each whitespace-separated word must appear SOMEWHERE in the haystack,
    // in any order — "dior sau" and "sau dior" both match "sauvage dior",
    // instead of the old single-substring check (which needed the exact
    // typed phrase to appear verbatim, so "dior sau" never matched anything
    // unless the words happened to sit in that order and adjacent).
    const terms = activeInput.value.toLowerCase().trim().split(/\s+/).filter(Boolean);
    (select._pzzOptionEls || []).forEach((option) => {
      // Match the hidden search string (code + name + brand, or title) — so a
      // banned brand stays findable even though its name is hidden on screen.
      const haystack = option.getAttribute('data-search') || '';
      const matches = terms.every((t) => haystack.includes(t));
      const next = matches ? 'block' : 'none';
      // PZZ INP: only touch the DOM when this option's visibility actually flips.
      // Most keystrokes move only a handful of options across the match boundary,
      // so the write cost drops from ~N per keystroke to just the ones that changed.
      if (option.style.display !== next) option.style.display = next;
      if (matches) {
        const oos = option.classList.contains('bundle-builder__option--out-of-stock') || this.isOptionOutOfStock(option);
        if (oos) {
          option.classList.add('bundle-builder__option--out-of-stock');
          option.style.pointerEvents = 'none';
        }
      }
    });
  }

  // PZZ INP: coalesce filtering to one pass per frame, so fast typing / paste /
  // IME composition can't queue a full rescan per keystroke.
  scheduleFilter(select, input) {
    if (this._filterPending) return;
    this._filterPending = true;
    requestAnimationFrame(() => {
      this._filterPending = false;
      this.filterOptions(select, input);
    });
  }

  closeSelect(select) {
    const trigger = select.querySelector('.bundle-builder__select-trigger');
    // Card mode's trigger content is the card itself (image/title/etc.),
    // already kept in sync by fillCard()/clearCard() at selection time —
    // nothing to restore via textContent here.
    if (select.dataset.mode !== 'card') {
      if (trigger.getAttribute('data-selected-value')) {
        trigger.textContent = trigger.getAttribute('data-product-title') || trigger.getAttribute('data-default-text');
      } else {
        trigger.textContent = trigger.getAttribute('data-default-text');
      }
    }
    select.classList.remove('bundle-builder__select--open');
    this.searchInput = null;
    document.body.classList.remove('no-scroll');
  }

  // Card mode: paint the chosen product into the big-card trigger. Reads the
  // cached _pzz* expando props buildOptionEl() set on the option — no re-fetch,
  // no re-deriving from `product` at click time.
  fillCard(trigger, optionElement) {
    const img = trigger.querySelector('[data-card-image]');
    const titleEl = trigger.querySelector('[data-card-title]');
    const inspiredEl = trigger.querySelector('[data-card-inspired]');
    const genderEl = trigger.querySelector('[data-card-gender]');
    const genderUse = trigger.querySelector('[data-card-gender-use]');

    // Same <img> the whole time (fixed 1:1 box, no layout shift) — swap its
    // src to the real photo; if that 404s, onerror falls straight back to the
    // placeholder already recorded on the element, never a broken-image icon.
    if (img) {
      const placeholder = img.getAttribute('data-placeholder-src') || '';
      const imgSrc = optionElement._pzzCardImage || '';
      img.onerror = placeholder ? () => { img.src = placeholder; img.onerror = null; } : null;
      img.style.opacity = '';
      img.src = imgSrc || placeholder;
    }

    // Full "ZAG Zodiak 019 AN" form here — the dropdown row above keeps the
    // shorter _pzzTitle.
    const titleText = optionElement._pzzCardTitle || optionElement._pzzTitle || optionElement.getAttribute('data-product-title') || '';
    if (titleEl) titleEl.textContent = titleText;
    if (img) img.alt = titleText;

    if (inspiredEl) {
      const txt = optionElement._pzzInspired || '';
      inspiredEl.textContent = txt;
      inspiredEl.hidden = !txt;
    }
    if (genderEl && genderUse) {
      const type = optionElement._pzzPillType || '';
      genderEl.classList.remove(
        'bundle-builder__card-gender--women',
        'bundle-builder__card-gender--men',
        'bundle-builder__card-gender--unisex'
      );
      if (type) {
        genderUse.setAttribute('href', '#pzz-gender-icon-' + type);
        genderEl.classList.add('bundle-builder__card-gender--' + type);
        genderEl.hidden = false;
      } else {
        genderEl.hidden = true;
      }
    }
    trigger.classList.add('bundle-builder__select-trigger--card-filled');
  }

  // Card mode: put a trigger back to its empty/placeholder state (used by
  // resetForm() after a successful add-to-cart).
  clearCard(trigger) {
    const img = trigger.querySelector('[data-card-image]');
    const titleEl = trigger.querySelector('[data-card-title]');
    const inspiredEl = trigger.querySelector('[data-card-inspired]');
    const genderEl = trigger.querySelector('[data-card-gender]');

    if (img) {
      const placeholder = img.getAttribute('data-placeholder-src') || '';
      img.onerror = null;
      img.style.opacity = '';
      if (placeholder) img.src = placeholder;
    }
    if (titleEl) titleEl.textContent = trigger.getAttribute('data-default-title') || 'Select a fragrance';
    if (inspiredEl) { inspiredEl.textContent = ''; inspiredEl.hidden = true; }
    if (genderEl) {
      genderEl.hidden = true;
      genderEl.classList.remove(
        'bundle-builder__card-gender--women',
        'bundle-builder__card-gender--men',
        'bundle-builder__card-gender--unisex'
      );
    }
    trigger.classList.remove('bundle-builder__select-trigger--card-filled');
  }

  // Card mode's summary bar ("N of M perfumes selected"). No-ops harmlessly
  // if the bar isn't in the markup (compact mode never renders it).
  updateSummaryCount() {
    const countEl = this.querySelector('[data-bundle-summary-count]');
    if (!countEl) return;
    const productSlots = Array.from(this.querySelectorAll('[data-bundle-slot="product"]'));
    const total = productSlots.length;
    const filled = productSlots.filter((slot) => {
      const t = slot.querySelector('.bundle-builder__select-trigger');
      return t && t.getAttribute('data-selected-value');
    }).length;
    const noun = total === 1 ? 'perfume' : 'perfumes';
    countEl.textContent = `${filled} of ${total} ${noun} selected`;
  }

  // One path for every selection: a click on a row AND a quick pick both land
  // here, so the trigger state the submit code reads (data-selected-value
  // etc.) is identical however a slot was filled. `optionElement` may be a
  // detached row built just for a quick pick (lists mount lazily); the real
  // row is highlighted now if it's mounted, or by renderOptionBatch() when it
  // mounts later.
  applySelection(select, optionElement) {
    const trigger = select.querySelector('.bundle-builder__select-trigger');
    const options = select.querySelector('.bundle-builder__options');
    const value = String(optionElement._pzzValue);

    if (select.dataset.mode === 'card') {
      this.fillCard(trigger, optionElement);
    } else {
      trigger.textContent = optionElement.getAttribute('data-original-text') || optionElement.getAttribute('data-product-title');
    }
    trigger.setAttribute('data-selected-value', value);
    trigger.setAttribute('data-product-handle', optionElement.getAttribute('data-product-handle'));
    trigger.setAttribute('data-product-title', optionElement.getAttribute('data-product-title'));
    trigger.classList.add('bundle-builder__select-trigger--selected');
    trigger.classList.remove('bundle-builder__select-trigger--error');

    // Scoped to THIS select's own list, so choosing in "Perfume Two" never
    // touches "Perfume One"'s highlighted row.
    const prev = options.querySelector('.bundle-builder__option--selected');
    if (prev) prev.classList.remove('bundle-builder__option--selected');
    const row = optionElement.isConnected
      ? optionElement
      : (select._pzzOptionEls || []).find((el) => String(el._pzzValue) === value);
    if (row) row.classList.add('bundle-builder__option--selected');

    select.classList.remove('bundle-builder__select--open');
    this.searchInput = null;
    document.body.classList.remove('no-scroll');
  }

  /* ---- quick picks (one-tap auto-fill, PZZ 2026-10-05) ------------------ */

  // Scent-family words matched against product tags. ONLY these words are
  // ever read from tags: tags also carry designer names, which must never
  // drive grouping or be shown (L'Oreal v Bellure house rule). Edit the lists
  // here to retune a style; the vocabulary comes from the live 100ml tags.
  // Keys must match the chip list in pzz-bundle-variant-picker.liquid.
  // Thin families (citrus/aquatic/leather/musk: under ~20 in stock each) are
  // folded into broader styles rather than given their own chip, so no chip
  // can come back nearly empty on a 10-sample bundle.
  static get QUICK_STYLES() {
    return {
      lucky:     [],
      fresh:     ['citrus', 'aquatic', 'green', 'aromatic', 'fougere'],
      floral:    ['floral'],
      sweet:     ['vanilla', 'gourmand', 'fruity'],
      woody:     ['woody'],
      warm:      ['amber', 'oriental', 'spicy'],
      fruity:    ['fruity'],
      summer:    ['citrus', 'aquatic', 'fruity', 'green'],
      winter:    ['amber', 'oriental', 'spicy', 'woody', 'leather'],
      everyday:  ['aromatic', 'citrus', 'green', 'fougere', 'musk'],
      datenight: ['amber', 'oriental', 'vanilla', 'musk', 'leather']
    };
  }

  // Exact comma tokens, so "women" is never read as "men". REST fallback
  // products carry product_type rather than productType.
  productGenders(product) {
    const raw = product.productType || product.product_type || '';
    return raw.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
  }

  productTagSet(product) {
    const raw = product.tags || [];
    const list = Array.isArray(raw) ? raw : String(raw).split(',');
    return list.map((t) => String(t).trim().toLowerCase());
  }

  matchesQuick(product, gender, style) {
    if (gender && !this.productGenders(product).includes(gender)) return false;
    const families = PzzBundleBuilder.QUICK_STYLES[style] || [];
    if (families.length) {
      const tags = this.productTagSet(product);
      if (!families.some((f) => tags.includes(f))) return false;
    }
    return true;
  }

  // Raw stock check from product data. The rows' --out-of-stock class can't
  // be used here: it reflects the CURRENT selections, which a quick pick is
  // about to replace. Same rule as a manual click: stock must exceed what's
  // already in the cart plus what this run has already picked.
  quickStockOk(product, usedInRun) {
    const v = product.variants && product.variants[0];
    if (!v || this.isVariantOutOfStock(v)) return false;
    if (v.quantityAvailable === null || v.quantityAvailable === undefined) return true;
    const id = String(v.id);
    const inCart = (this._lastCartQuantities && this._lastCartQuantities[id]) || 0;
    return v.quantityAvailable > inCart + (usedInRun[id] || 0);
  }

  // Fills every product slot (then any free-gift slot, from its own pool).
  // Each slot tries, in order: exact gender + style, then the same gender in
  // any style, then any gender; always unique within the bundle first. A
  // repeat only happens if fewer in-stock perfumes exist than slots.
  // Picks from the already-loaded product data, NOT by mounting every row of
  // every list: a 10-slot sample bundle would otherwise build ~3,000 rows on
  // one tap and undo the lazy-mount INP work.
  runQuickPick() {
    const gender = this._quickGender || '';
    const style = this._quickStyle || 'lucky';
    const slots = Array.from(this.querySelectorAll('[data-bundle-slot="product"], [data-bundle-slot="free"]'));
    const usedInRun = {};
    const picked = new Set();
    let relaxed = 0;
    let missed = 0;
    const randomFrom = (arr) => arr[Math.floor(Math.random() * arr.length)];

    slots.forEach((select, i) => {
      const decorated = select._pzzDecorated || [];
      // "Mixed" = alternate women's and men's scents across the slots.
      const slotGender = gender === 'mixed' ? (i % 2 === 0 ? 'women' : 'men') : gender;
      const tiers = [[slotGender, style], [slotGender, 'lucky'], ['', 'lucky']];

      let idx = -1;
      let tierUsed = 0;
      for (let t = 0; t < tiers.length && idx === -1; t++) {
        const [g, s] = tiers[t];
        const pool = [];
        decorated.forEach((d, k) => {
          const vid = String(d.product.variants[0].id);
          if (!picked.has(vid) && this.matchesQuick(d.product, g, s) && this.quickStockOk(d.product, usedInRun)) pool.push(k);
        });
        if (pool.length) { idx = randomFrom(pool); tierUsed = t; }
      }
      if (idx === -1) {
        const pool = [];
        decorated.forEach((d, k) => { if (this.quickStockOk(d.product, usedInRun)) pool.push(k); });
        if (pool.length) { idx = randomFrom(pool); tierUsed = 3; }
      }
      if (idx === -1) { missed++; return; }
      if (tierUsed > 0) relaxed++;

      const { product, label } = decorated[idx];
      const vid = String(product.variants[0].id);
      usedInRun[vid] = (usedInRun[vid] || 0) + 1;
      picked.add(vid);
      // Rows mount in decorated order, so index k IS row k when mounted.
      const el = (select._pzzOptionEls || [])[idx] || this.buildOptionEl(product, label);
      this.applySelection(select, el);
    });

    this.scheduleDisableOptions();
    this.updateSummaryCount();
    this.setQuickNote(relaxed, missed);
  }

  setQuickNote(relaxed, missed) {
    const note = this.querySelector('[data-quick-note]');
    if (!note) return;
    if (missed) {
      note.textContent = 'Not enough perfumes are in stock to fill every slot, so please choose the rest below.';
    } else if (relaxed) {
      note.textContent = 'We didn’t have enough exact matches in stock, so ' + relaxed +
        (relaxed === 1 ? ' pick is a close match.' : ' picks are close matches.') + ' You can swap any of them below.';
    } else {
      note.textContent = 'Done. Tap the style again for a new mix, or swap any perfume below.';
    }
  }

  syncQuickChips() {
    this.querySelectorAll('[data-quick-gender]').forEach((c) => {
      c.setAttribute('aria-pressed', String(c.getAttribute('data-quick-gender') === this._quickGender));
    });
    this.querySelectorAll('[data-quick-style]').forEach((c) => {
      c.setAttribute('aria-pressed', String(c.getAttribute('data-quick-style') === this._quickStyle));
    });
  }

  // How many style chips show before "+ N more" is MEASURED, not fixed: as
  // many as fit on one line beside the link, so wide screens show more and
  // phones fewer. Measuring briefly un-hides every chip (.is-measuring) and
  // reads widths in the same frame, so nothing flickers. Skipped while the
  // shopper has the row expanded.
  layoutQuickStyles() {
    const row = this.querySelector('[data-quick-style-row]');
    if (!row || row.classList.contains('is-expanded')) return;
    const chips = Array.from(row.querySelectorAll('[data-quick-style]'));
    const more = row.querySelector('[data-quick-more]');
    if (!chips.length) return;

    row.classList.add('is-measuring');
    if (more) more.textContent = '+ ' + chips.length + ' more'; // widest label it can have
    const available = row.clientWidth;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const widths = chips.map((c) => c.getBoundingClientRect().width);
    const moreWidth = more ? more.getBoundingClientRect().width : 0;
    row.classList.remove('is-measuring');

    const allWidth = widths.reduce((a, w) => a + w, 0) + gap * (widths.length - 1);
    let fit = chips.length;
    if (allWidth > available) {
      fit = 0;
      let used = moreWidth;
      for (let i = 0; i < widths.length; i++) {
        const next = used + gap + widths[i];
        if (next > available) break;
        used = next;
        fit++;
      }
      fit = Math.max(1, fit);
    }

    chips.forEach((c, i) => c.classList.toggle('bundle-builder__quick-chip--extra', i >= fit));
    const hiddenCount = chips.length - fit;
    if (more) {
      more.hidden = hiddenCount === 0;
      more.dataset.labelMore = '+ ' + hiddenCount + ' more';
      more.textContent = more.dataset.labelMore;
    }
  }

  // Gender chips toggle (tap the active one again = any gender). Style chips
  // always re-roll, so tapping the active style again gives a fresh mix.
  // Choosing only a gender implies "Lucky dip" for the style.
  setupQuickPicks() {
    const root = this.querySelector('[data-quick-picks]');
    if (!root) return;
    this._quickGender = '';
    this._quickStyle = '';

    this.layoutQuickStyles();
    // Re-measure when the panel's WIDTH changes (rotation, resize). Height
    // changes from collapsing/expanding are ignored, so this can't loop.
    if ('ResizeObserver' in window) {
      let lastWidth = Math.round(root.getBoundingClientRect().width);
      this._quickResizeObserver = new ResizeObserver((entries) => {
        const w = Math.round(entries[0].contentRect.width);
        if (w === lastWidth) return;
        lastWidth = w;
        requestAnimationFrame(() => this.layoutQuickStyles());
      });
      this._quickResizeObserver.observe(root);
    }
    // Web fonts can land after first measure and change chip widths.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.layoutQuickStyles());
    root.addEventListener('click', (e) => {
      // "+ N more" / "Show fewer": reveals the extra style chips in place.
      // An active extra chip stays visible even when collapsed (CSS), so the
      // current choice never disappears from view.
      const more = e.target.closest('[data-quick-more]');
      if (more) {
        const row = more.closest('[data-quick-style-row]');
        const open = !row.classList.contains('is-expanded');
        row.classList.toggle('is-expanded', open);
        more.setAttribute('aria-expanded', String(open));
        if (open) more.textContent = more.dataset.labelLess;
        else this.layoutQuickStyles(); // re-fits the line and resets the label
        return;
      }
      const chip = e.target.closest('[data-quick-gender], [data-quick-style]');
      if (!chip) return;
      if (chip.hasAttribute('data-quick-gender')) {
        const g = chip.getAttribute('data-quick-gender');
        this._quickGender = this._quickGender === g ? '' : g;
        if (!this._quickStyle) this._quickStyle = 'lucky';
      } else {
        this._quickStyle = chip.getAttribute('data-quick-style');
      }
      this.syncQuickChips();
      this.runQuickPick();
    });
  }

  setupEventListeners() {
    this.selects.forEach((select) => {
      const trigger = select.querySelector('.bundle-builder__select-trigger');
      const options = select.querySelector('.bundle-builder__options');

      // PZZ INP: lazily mount further batches as the shopper scrolls the
      // (now windowed) panel, instead of every row already being mounted.
      let scrollPending = false;
      options.addEventListener('scroll', () => {
        if (scrollPending || select._pzzAllMounted) return;
        scrollPending = true;
        requestAnimationFrame(() => {
          scrollPending = false;
          if (options.scrollTop + options.clientHeight >= options.scrollHeight - 300) {
            this.renderOptionBatch(select);
          }
        });
      }, { passive: true });

      const isCard = select.dataset.mode === 'card';

      if (isCard) {
        // Card mode has no open/closed state at all — the search input and
        // results list are static markup, always visible (see
        // pzz-bundle-variant-picker.liquid's .bundle-builder__card-panel).
        // Each card's input is bound directly and filters ONLY its own list;
        // unlike compact mode there's no shared/singleton this.searchInput to
        // fight over, since multiple cards can be searched independently at
        // the same time. (The wheel/click handlers below still apply to it —
        // this only replaces the compact "click trigger to open" behaviour.)
        const cardInput = select.querySelector('[data-card-search-input]');
        if (cardInput) {
          cardInput.addEventListener('input', () => {
            if (!select._pzzAllMounted) this.mountAllOptions(select);
            this.scheduleFilter(select, cardInput);
          });
          // Clicking anywhere on the card (image/title/etc.) jumps focus to
          // its search field — a convenience, not a toggle.
          trigger.addEventListener('click', () => cardInput.focus());
        }
      } else {
        trigger.addEventListener('click', (e) => {
          if (!select.classList.contains('bundle-builder__select--open')) {
            this.selects.forEach((other) => {
              if (other !== select) other.classList.remove('bundle-builder__select--open');
            });
            select.classList.add('bundle-builder__select--open');
            document.body.classList.add('no-scroll');
            this.activeSelect = select;

            let input = trigger.querySelector('input');
            if (!input) {
              input = document.createElement('input');
              input.type = 'text';
              input.placeholder = 'Search products…';
              trigger.innerHTML = '';
              trigger.appendChild(input);
              // PZZ INP: bind once on the fresh input; coalesced to one pass/frame.
              input.addEventListener('input', () => {
                // Search must see the whole catalogue, not just the
                // scrolled-to window — mount everything once, on first keystroke.
                if (!select._pzzAllMounted) this.mountAllOptions(select);
                this.scheduleFilter(select);
              });
            }
            this.searchInput = input;
            // PZZ FIX (2026-09-03): iOS Safari zooms the page on focus when it judges an
            // input's font-size too small -- and it makes that call at the instant focus()
            // runs. pzz-style.css sets this input to 16px (the safe threshold), but this
            // input is created and inserted into the DOM in the lines just above, in the
            // same synchronous tick as this focus() call -- before the browser has painted
            // a frame with the new node's real computed style. iOS's zoom heuristic sees it
            // pre-style, not the settled 16px, and zooms anyway. Deferring focus() to the
            // next frame lets that first paint happen first, so iOS reads the correct size.
            requestAnimationFrame(() => input.focus());
            this.resetSearch(select);
          } else {
            this.searchInput = trigger.querySelector('input');
            if (this.searchInput) this.searchInput.focus();
          }
          e.stopPropagation();
        });
      }

      options.addEventListener('wheel', (e) => {
        if (e.deltaY > 0 && options.scrollTop + options.clientHeight >= options.scrollHeight) {
          e.preventDefault();
        } else if (e.deltaY < 0 && options.scrollTop <= 0) {
          e.preventDefault();
        }
      });

      options.addEventListener('click', (e) => {
        const optionElement = e.target.closest('.bundle-builder__option');
        if (!optionElement) return;

        const optionValue = optionElement._pzzValue;
        const optionStock = optionElement._pzzStock !== undefined ? optionElement._pzzStock : 999999;
        const cartQuantity = optionElement._pzzCartQty || 0;
        const isInventoryManaged = optionElement._pzzInventoryManaged;

        const currentlySelected = this.selects
          .map((s) => s.querySelector('.bundle-builder__select-trigger').getAttribute('data-selected-value'))
          .filter((value) => value === optionValue).length;

        let canSelect = true;
        if (isInventoryManaged) {
          canSelect = optionStock > (cartQuantity + currentlySelected);
        }

        if (canSelect && !optionElement.classList.contains('bundle-builder__option--out-of-stock')) {
          this.applySelection(select, optionElement);
          this.scheduleDisableOptions();
          this.updateSummaryCount();
        } else {
          console.log('Product is out of stock or limit reached');
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.bundle-builder__select')) {
        this.selects.forEach((select) => {
          if (select.classList.contains('bundle-builder__select--open')) {
            this.closeSelect(select);
          }
        });
      }
    });

    this.formEl.addEventListener('submit', (event) => {
      event.preventDefault();
      this.handleSubmit();
    });
  }

  handleSubmit() {
    if (this._submitting) return; // ADD — ignore clicks while adding
    const bundleSize = this.config.bundleSize;
    const freeProductQty = this.config.freeProductQty;

    const selectedProductIds = [];
    const selectedProductTitles = [];

    let hasEmpty = false;

    this.selects.forEach((select) => {
      const trigger = select.querySelector('.bundle-builder__select-trigger');
      const id = trigger.getAttribute('data-selected-value');
      const title = trigger.getAttribute('data-product-title');

      if (id && id.trim() !== '') {
        // Valid field: keep it green, ensure no error state.
        selectedProductIds.push(id);
        selectedProductTitles.push(title || 'Unknown Product');
        trigger.classList.remove('bundle-builder__select-trigger--error');
      } else {
        // Empty field: (re)trigger the red + shake. PZZ INP: restart the CSS
        // animation via rAF instead of a forced-synchronous-layout read
        // (`void trigger.offsetWidth`) — same "remove then re-add the class"
        // restart trick, but no layout thrash when several fields are empty
        // at once (this loop used to read layout once per empty field).
        hasEmpty = true;
        trigger.classList.remove('bundle-builder__select-trigger--error');
        requestAnimationFrame(() => trigger.classList.add('bundle-builder__select-trigger--error'));
      }
    });

    const bundleProductsSelected = selectedProductIds.slice(0, bundleSize).length;
    const freeProductsSelected = freeProductQty > 0 ? selectedProductIds.slice(bundleSize).length : 0;

    // No alerts — the red fields + shake are the only feedback.
    if (hasEmpty) return;
    if (bundleProductsSelected < bundleSize) return;
    if (!this.autoGift && freeProductQty > 0 && freeProductsSelected < freeProductQty) return;

    const giftCheckbox = this.querySelector('[data-gift-checkbox]');
    const giftPackagingChecked = giftCheckbox ? giftCheckbox.checked : false;

    const bundleId = this.generateBundleId();
    const bundleProductList = selectedProductTitles
      .slice(0, bundleSize)
      .map((title, index) => `${index + 1}. ${title}`)
      .join(' | ');

    const giftIncluded = giftPackagingChecked || this.config.autoIncludeGiftBox;

    const itemsToAdd = selectedProductIds.map((id) => ({
      id,
      quantity: 1,
      properties: {
        'Bundle ID': bundleId,
        '_Bundle Products': bundleProductList,
        '_Gift Packaging': giftIncluded ? 'Yes' : 'No',
        '_Bundle Notes': ''
      }
    }));

    if (giftIncluded && this.config.packagingVariantId) {
      itemsToAdd.push({
        id: this.config.packagingVariantId,
        quantity: 1,
        properties: {
          'Bundle ID': bundleId,
          '_Bundle Products': bundleProductList,
          '_Gift Packaging': 'Yes',
          '_Bundle Notes': ''
        }
      });
    }

    // Auto-included single-product gift (the card). Added like the packaging box;
    // the discount code applied in addItemsToCart makes it land free.
    if (this.autoGift) {
      itemsToAdd.push({
        id: this.autoGift.id,
        quantity: 1,
        properties: {
          'Bundle ID': bundleId,
          '_Bundle Products': bundleProductList,
          '_Gift Packaging': giftIncluded ? 'Yes' : 'No',
          '_Free Gift': 'Yes',
          '_Bundle Notes': ''
        }
      });
    }

    this.setButtonLoading(true);
    this.addItemsToCart(itemsToAdd, bundleId);
  }

  /*
   * Horizon 4.1 add-to-cart handshake.
   * ----------------------------------
   * Cart events moved from '@theme/events' (gone) to Shopify's standard events
   * module '@shopify/events' (CartLinesUpdateEvent). The protocol is:
   *   1. dispatch a CartLinesUpdateEvent carrying a *pending* promise,
   *   2. POST /cart/add.js requesting the section HTML for every
   *      <cart-items-component> on the page,
   *   3. resolve the promise with { cart, detail: { sections, items, itemCount } }.
   * The theme then updates the count bubble, morphs the drawer/cart, and (because
   * action:'add') auto-opens the drawer — identical to adding a normal product.
   * This replaces the old refreshHorizonCart()/injected-module approach, which
   * dispatched the now-dead CartUpdateEvent / 'cart:update' and so never rendered.
   */
  async addItemsToCart(items, bundleId) {
    // Section ids of every cart-items-component (cart page and/or drawer).
    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const totalQuantity = items.reduce((sum, it) => sum + (it.quantity || 1), 0);
    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    const updateUrl = (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update';

    // Load Shopify's standard events module (importmap: '@shopify/events').
    // Dynamic import() honours the importmap from this classic script — no need
    // for the old injected <script type="module"> trick.
    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {
      console.warn('[pzz-bundle-builder] @shopify/events unavailable; cart UI may not refresh without reload.', e);
    }

    // Dispatch the pending-promise event up front so listeners attach before the
    // network round-trip (matches the theme's own product-form behaviour).
    let deferred = null;
    if (events && events.CartLinesUpdateEvent) {
      deferred = events.CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((it) => ({ merchandiseId: it.id, quantity: it.quantity || 1 })),
        promise: deferred.promise
      }));
    }

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      }).then((res) => res.json());

      // Shopify add errors come back as { status, message, description, ... }.
      if (response && response.status) {
        console.error('Cart add error:', response);
        if (deferred && events.CartErrorEvent) {
          this.dispatchEvent(new events.CartErrorEvent({
            error: response.description || response.message || 'Add to cart failed',
            code: 'INVALID'
          }));
        }
        if (deferred) deferred.reject(new Error(response.description || response.message || 'Add to cart failed'));
        this.setButtonLoading(false);
        return;
      }

      try {
        const bundleInfo = {
          id: bundleId,
          name: this.config.bundleProductTitle,
          totalPrice: this.config.bundleTotalPrice,
          itemCount: items.length,
          timestamp: new Date().toISOString()
        };
        const existing = JSON.parse(sessionStorage.getItem('activeBundles') || '[]');
        existing.push(bundleInfo);
        sessionStorage.setItem('activeBundles', JSON.stringify(existing));
      } catch (e) {
        console.error('Error storing bundle info:', e);
      }

      // The /cart/add.js sections were rendered BEFORE the discount was applied,
      // so they show pre-discount prices (e.g. the free item at full price until
      // reload). Apply the discount first, THEN re-render the sections via a no-op
      // /cart/update.js so the resolved HTML already reflects the discount. This
      // call also returns the full cart, giving us the true count for the bubble.
      let cart = null;
      let sections = response.sections;

      if (this.config.discountCode && this.config.discountCode !== 'DISCOUNT_CODE') {
        try {
          await this.applyDiscount(this.config.discountCode);
        } catch (e) {
          console.warn('[pzz-bundle-builder] discount apply failed (continuing):', e);
        }
      }

      try {
        const updated = await fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: {}, sections: sectionIds.join(','), sections_url: window.location.pathname })
        }).then((r) => r.json());
        cart = updated;
        if (updated && updated.sections) sections = updated.sections;
      } catch (e) {
        console.error('[pzz-bundle-builder] post-discount re-render failed; falling back:', e);
        // Fallback: at least get the cart for the count bubble.
        try {
          cart = await fetch(cartUrl + '.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin'
          }).then((r) => r.json());
        } catch (e2) {
          console.error('[pzz-bundle-builder] cart re-read failed:', e2);
        }
      }

      // Resolve the promise -> theme refreshes bubble + drawer + cart page.
      if (deferred) {
        let cartSummary;
        try {
          cartSummary = (cart && events.CartLinesUpdateEvent.createCartFromAjaxResponse)
            ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
            : { totalQuantity: cart ? cart.item_count : totalQuantity };
        } catch (e) {
          cartSummary = { totalQuantity: cart ? cart.item_count : totalQuantity };
        }
        deferred.resolve({
          cart: cartSummary,
          detail: {
            items: cart ? cart.items : [],
            itemCount: totalQuantity,
            sections: sections,
            source: 'pzz-bundle-builder',
            didError: false
          }
        });
      }

      this.setButtonLoading(false);
      this.playAddedBurst();
      this.flyToCart();
      this.fetchCartItems();
      this.resetForm();
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (deferred) deferred.reject(error);
      this.setButtonLoading(false);
    }
  }

  setButtonLoading(state) {
    this._submitting = state; // re-entrancy guard for handleSubmit
    const btn = this.querySelector('[data-bundle-submit]');
    if (!btn) return;
    btn.classList.toggle('is-loading', state);
    btn.setAttribute('aria-busy', state ? 'true' : 'false');
    // Intentionally NOT setting btn.disabled — the theme greys out disabled
    // buttons (the "dead grey" we're fixing). .is-loading kills pointer-events,
    // and _submitting blocks a second submit while the request is in flight.
  }

  flyToCart() {
    // Reuse Horizon's own <fly-to-cart> element (defined globally) so the
    // effect matches normal products exactly. It takes a source and a
    // destination as element properties, measures both, animates along a
    // bezier curve, then removes itself.
    try {
      const button = this.querySelector('[data-bundle-submit]');
      const cartIcon = document.querySelector('cart-icon');
      if (!button || !cartIcon || !customElements.get('fly-to-cart')) return;

      const fly = document.createElement('fly-to-cart');
      fly.classList.add('fly-to-cart--quick');
      // Match the button's footprint so the flying chip looks proportional.
      const rect = button.getBoundingClientRect();
      fly.style.setProperty('--width', Math.min(rect.width, 48) + 'px');
      fly.style.setProperty('--height', '40px');
      fly.style.backgroundColor = getComputedStyle(button).backgroundColor;
      fly.source = button;
      fly.destination = cartIcon;
      document.body.appendChild(fly);
    } catch (e) {
      console.error('PZZ fly-to-cart failed:', e);
    }
  }

  playAddedBurst() {
    const btn = this.querySelector('[data-bundle-submit]');
    if (!btn) return;
    // Horizon's global CSS animates the checkmark-burst when data-added="true"
    // is present on the .add-to-cart-button. Set it, then clear it so it can
    // replay on the next add.
    btn.setAttribute('data-added', 'true');
    setTimeout(() => btn.removeAttribute('data-added'), 1600);
  }

  resetForm() {
    this.selects.forEach((select) => {
      const trigger = select.querySelector('.bundle-builder__select-trigger');
      if (select.dataset.mode === 'card') {
        this.clearCard(trigger);
      } else {
        trigger.textContent = trigger.getAttribute('data-default-text');
      }
      trigger.removeAttribute('data-selected-value');
      trigger.removeAttribute('data-product-handle');
      trigger.removeAttribute('data-product-title');
      trigger.classList.remove('bundle-builder__select-trigger--selected');
      const selectedOption = select.querySelector('.bundle-builder__option--selected');
      if (selectedOption) selectedOption.classList.remove('bundle-builder__option--selected');
    });
    const giftCheckbox = this.querySelector('[data-gift-checkbox]');
    const giftToggle = this.querySelector('.bundle-builder__gift-toggle');
    if (giftCheckbox) giftCheckbox.checked = false;
    if (giftToggle) giftToggle.classList.remove('bundle-builder__gift-toggle--active');
    this._quickGender = '';
    this._quickStyle = '';
    this.syncQuickChips();
    const quickNote = this.querySelector('[data-quick-note]');
    if (quickNote) quickNote.textContent = '';
    this.scheduleDisableOptions();
    this.updateSummaryCount();
    this.updateSummaryPrice();
  }

  applyDiscount(discountCode) {
    const encoded = encodeURIComponent(discountCode);
    return fetch(`/discount/${encoded}`, { method: 'GET' })
      .then((response) => {
        if (!response.ok) console.log('Discount code may not be active or valid');
      })
      .catch((error) => console.error('Error applying discount:', error));
  }
}

if (!customElements.get('pzz-bundle-builder')) {
  customElements.define('pzz-bundle-builder', PzzBundleBuilder);
}

/* Bundle "Choose" navigation.
 * The bundle Choose control (snippets/pzz-quick-add-bundle.liquid) is a plain
 * <button data-pzz-bundle-href="..."> that must navigate to the product page.
 * This listener lives here — in the globally-loaded script — rather than in a
 * {% javascript %} block in the snippet, because product cards in client-fetched
 * sections (product-recommendations, recently viewed) inject their HTML after
 * load, so any script inside those cards never executes. One delegated listener
 * on document catches clicks on those injected buttons too.
 */
if (!window.__pzzBundleChooseNav) {
  window.__pzzBundleChooseNav = true;
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-pzz-bundle-href]');
    if (!trigger) return;
    event.preventDefault();
    window.location.href = trigger.getAttribute('data-pzz-bundle-href');
  });
}